import { cardEffect, cardFaction, cardName, cardRealm, cardRecord, cardSubTypes, hasRealm } from "./cards";
import { displayName } from "../display-names.generated";
import { stat, type RuntimeObject } from "./state";
import { audio } from "../fx/audio";
import { CARD_FRAMES, REALM_COLOUR } from "../card-frames.generated";
import { KEYWORD_INFO } from "../effect-keywords.generated";
// ⚠️ The keyword is read from APPLIED EFFECTS, never from the printed card text - taunt is granted
// at runtime by Rally effects and removed by a silence. See the badge below.
import { hasKeyword, liveConditions, roleLabel, type BoardRole } from "./rules";
import { attachZoom, setZoomRenderer } from "../ui/cardZoom";
import { setRichTip } from "../ui/tip";
// ⭐ Where a card can be won - generated from the campaign data, never hand-written.
import { CARD_SOURCES, RANDOM_CARD_SOURCE } from "../card-sources.generated";

/**
 * A CARD. Our art, our frame, our badges.
 *
 * 🚨 **THE FIRST VERSION OF THIS WAS A TEXT "CHIP" AND ALEX REJECTED IT ON SIGHT:** *"You must reuse
 * our cards and the cards artwork. I do not know what this is, but it is not the right direction if
 * it is supposed to represent our cards."* He was right, and the artefacts were all already there:
 *
 *     art        art-src/card-art/<artUrl>.png    -> matches 171 of 171 cards BY artUrl
 *     mask       Card_Mask_None                   -> the shape the art is clipped to
 *     shine      Card_Shine_None                  -> the gloss over it
 *     badges     Card_Attribute_Attack / _Health  -> struck wedge / closed ring
 *
 * All four were redesigned as ONE object by `tools/redesign_card_frame.py`. Drawing a new frame here
 * would be a fifth shape that agrees with none of them.
 *
 * ⚠️ **`artUrl` IS THE KEY, NOT THE TITLE.** Matching on `title` hits ZERO of 171 - measured. A title
 * lookup renders a board of blank frames and reads as missing art rather than as a wrong key.
 *
 * ⚠️ **THE BADGES ARE NOT INTERCHANGEABLE CIRCLES.** From `redesign_card_frame.py`: *"attack is a
 * struck wedge, health is a closed ring"*, so a player can tell them apart at a glance and at any
 * size, which the original's near-identical badges could not manage. Do not swap them.
 *
 * ⚠️ **`cardEffect` IS THE RULES TEXT.** The original stores it in a field called `description`; the
 * flavour lives in `tooltip`. See `cards.ts` - that inversion is the single most expensive naming
 * trap in this project.
 */

export interface CardOptions {
  /**
   * Do not attach the hover-to-inspect preview.
   *
   * ⚠️ Set by the preview itself. Without it the zoom draws a card, that card attaches a zoom, and
   * hovering the preview opens a preview of the preview.
   */
  noZoom?: boolean;

  /**
   * This exact copy is MINTED. Draws the foil finish. See where it is built, below.
   *
   * ⛔ **A PROPERTY OF THE OWNED ENTITY, NOT OF THE CARD.** The caller must know whose copy this is;
   * deriving it from the card id inside the renderer would gild an opponent's copy of a card you
   * own.
   */
  foil?: boolean;

  /** Show the mulligan selection affordance and reflect `selected`. */
  selectable?: boolean;
  selected?: boolean;
  /**
   * This selection means "throw this card back" (the mulligan), not "act with it" (the game).
   *
   * ⚠️ Selection is one CLASS but two MEANINGS. Without this flag the mulligan's REPLACE badge
   * appeared on a selected attacker mid-match, which is a rule that does not exist in the game.
   */
  replacing?: boolean;
  /**
   * The card can be PLAYED right now.
   *
   * ⚠️ Presentation only. The server re-checks affordability and refuses - this exists so a player
   * is not invited to press something that will be refused, not so the client decides legality.
   */
  playable?: boolean;
  /**
   * WHY it cannot be played, in words the player can act on.
   *
   * ⚠️ Prefer the SERVER's reason. It knows which colour is short and by how much; the client only
   * knows that something said no.
   */
  refusal?: string | undefined;
  /**
   * The COLOURED Foxfire this card demands, as realm -> count.
   *
   * ⭐ **SHOWN BEFORE THE REFUSAL, NOT ONLY AFTER IT.** Burning grants one token of the burned
   * card's OWN colour, so choosing what to feed the fire is the decision typed Foxfire exists to
   * create - and it cannot be made if the requirement is invisible until after you have paid.
   */
  requires?: Record<string, number> | null | undefined;
  /**
   * What this card is DOING on the table. Drawn as a role plinth under the card.
   *
   * ⚠️ Only ever set for cards ON THE TABLE. A hand card has no role yet - it has not done anything -
   * and labelling one would be a promise about a play that has not happened.
   */
  role?: BoardRole | undefined;
  onToggle?: () => void;
  /**
   * Feed this card to the Foxfire: burn it from hand for its cost in Foxfire.
   *
   * ⚠️ Offered as its OWN control rather than a modifier on the card, because burning and playing
   * are different decisions with different consequences and a player must not confuse them.
   */
  onBurn?: () => void;
  /**
   * A board creature rather than a hand card.
   *
   * ⭐ **A CARD ON THE TABLE IS A DIFFERENT OBJECT FROM A CARD IN HAND, AND EVERY CARD GAME KNOWS
   * IT.** In hand you are READING it - cost, name, full rules text - to decide. On the table you are
   * TRACKING it: what it hits for, what kills it, whether it can act. Rendering the hand card at
   * slot size crushed the name, the rules text and the badges into an unreadable stamp.
   */
  mini?: boolean;
  /** Runtime object id, making the card draggable into the Foxfire basin. */
  dragId?: number;
  /**
   * This enemy creature is shielded from the current attacker by the taunt rule.
   *
   * ⭐ **DRAWN, NOT MERELY DISABLED.** The engine refuses an attack that skips a taunter, and a
   * refused command is swallowed - so an un-drawn restriction is a click that does nothing. Showing
   * the guard turns "the game is broken" into "that one is protected", which is a rule the player
   * can play around.
   */
  guarded?: boolean;
}

/**
 * A realm's name as a player reads it on a card: short enough for the chip, still unambiguous.
 *
 * ⭐ DERIVED FROM THE KEY, never a hand-written map. The set of realms is owned by the generated
 * data and is open-ended by design; a lookup table here would be a second owner that goes stale the
 * first time a realm is added or renamed. `The_Verdant_Hollow` -> `Verdant Hollow`.
 */
function realmShortName(realm: string): string {
  // ⛔ ONE DISTINCTIVE WORD, because the full name does not fit and a TRUNCATED name is worse than
  // no name at all. Measured in `docs/captures/web/card-realms.png` on the first pass: "VERDANT
  // HOLL...", "SCORCHED EXP...", "DOMAIN OF NE...". The chip exists precisely because colour alone
  // is not a signal, so an ellipsis defeats the whole point of adding it.
  //
  // ⭐ DERIVED, NOT A LOOKUP TABLE. Drop a leading article, drop a leading "<Word> of", take the
  // first word. The set of realms is open-ended by design and a hand-written map here would be a
  // second owner that rots the first time one is added.
  //
  //   The_Verdant_Hollow    -> Verdant      The_Spirit_Veil  -> Spirit
  //   The_Scorched_Expanse  -> Scorched     The_Ironworks    -> Ironworks
  //   Domain_of_Negafook    -> Negafook     Neutral          -> Neutral
  //
  // All six stay distinct. If a future realm collides on its first word, the fix is the NAME, not a
  // special case here.
  return realm
    .replace(/^The_/, "")
    .replace(/^[A-Za-z]+_of_/, "")
    .split("_")[0] ?? realm;
}

export function cardEl(c: RuntimeObject, opts: CardOptions = {}): HTMLElement {
  const name = cardName(c.DataId);
  const effect = cardEffect(c.DataId);

  const root = document.createElement(opts.selectable ? "button" : "div");
  root.className = opts.mini ? "card card--mini" : "card";
  // ⭐ The runtime id, so an animation driven by a server event can find the card it belongs to.
  // Without it the board would have to guess which element an `ObjectHitEvent` meant.
  if (typeof c.Id === "number") root.dataset["obj"] = String(c.Id);

  // ⭐ **WHICH CARD THIS IS, AS A MACHINE CHANNEL.** `data-obj` is the runtime instance; this is the
  // DATABASE id, which is what authored content is written in - a puzzle's `enemyBoard` says "146",
  // a deck lists ids, and a gate asking "is the authored creature on the board?" has no other way to
  // answer.
  //
  // ⚠️ **IT IS SET UNCONDITIONALLY, AND `data-label` IS THE REASON.** The name is attached ONLY on
  // selectable cards, so an enemy creature - never selectable except while being targeted - carried
  // no identity whatsoever. `verify_puzzle.py` could see that the enemy had exactly one creature and
  // could not tell WHICH, so a puzzle placing the wrong body would have passed. Identity a gate
  // needs must not be a side effect of an interaction state.
  if (c.DataId !== undefined) root.dataset["card"] = String(c.DataId);

  // ⭐ **THE REALM FRAME. We made a set per realm and they carry mechanical meaning**, because typed
  // Foxfire prices a card in its own realm's colour - so the frame IS the cost's context, not
  // decoration. Alex, 2026-08-14: *"we also made 4 sets of cards one for each realm. Make sure we
  // use our full 4 sets."*
  //
  // ⛔ **NO FALLBACK FRAME.** An unknown realm is marked as unknown (`data-realm` absent) rather
  // than dressed in the neutral frame: a card wearing the wrong realm looks entirely deliberate and
  // misreports the one fact its cost depends on. Realms will also arrive later as NFTs, so the set
  // is open-ended by design and this file must never enumerate it.
  const realm = cardRealm(c.DataId);
  const frame = realm ? CARD_FRAMES[realm] : undefined;
  const frameUrl = opts.mini ? frame?.regular : (frame?.full ?? frame?.regular);
  if (realm) {
    root.dataset["realm"] = realm;
    // ⭐ **A CARD MUST SAY WHICH REALM IT IS FROM, AT A GLANCE.** Alex, 2026-08-14: *"the card
    // frames, or page background or something needs to tell us these are different factions."* He
    // is right and it is not decoration - the realm decides what the card COSTS you (the loyalty
    // wheel) and whether you may take it at all, so it is the second thing a player reads after the
    // cost. The colour is the generated one, so it is the same colour the builder, the help page
    // and the deck list use.
    const colour = REALM_COLOUR[realm];
    if (colour) root.style.setProperty("--realm", colour);
  }
  if (frameUrl) {
    root.style.setProperty("--card-frame", `url("${frameUrl}")`);
    root.classList.add("card--framed");
  }
  if (opts.guarded) root.classList.add("card--guarded");

  // ⭐ **EVERY CARD, EVERYWHERE, IS INSPECTABLE.** Attached here rather than per screen, because the
  // screen that needed it most - the deck builder - is exactly the one a per-screen wiring forgets.
  // Hold the pointer on a card and the full face opens beside it. See `ui/cardZoom.ts`.
  //
  // ⚠️ Only for MINI cards. A full card face is already the thing the preview would show, so
  // previewing it is a copy of itself sitting next to itself.
  if (opts.mini && !opts.noZoom) attachZoom(root, c);

  // ⭐ **A TAUNTER SAYS SO, ON THE BOARD, ALL THE TIME.**
  //
  // 🚨 **TAUNT WAS ONLY EVER VISIBLE MID-ATTACK, AND ON THE WRONG CARDS.** `card--guarded` hatches
  // the cards a taunter is PROTECTING, and only while an attacker is selected. So a player looking
  // at the board could not tell which enemy creature was the wall - the single most important piece
  // of information for planning a turn - and only discovered it by picking a target and being
  // refused. Alex, 2026-08-15, on exactly this: *"why have all these cards? do they affect the
  // shield? like protection? it feels like the game mechanics are off."* The mechanic was working;
  // it was invisible.
  //
  // ⚠️ **READ FROM THE APPLIED EFFECTS, NOT FROM THE CARD DATABASE.** Taunt can be granted at
  // runtime (`Rally: adjacent creatures gain taunt`, `Gains Taunt when healed`) and removed by a
  // silence - `The Second Death` silences the whole table every round. A badge driven by the printed
  // card text would mark a silenced creature as a wall and miss every granted one.
  if (hasKeyword(c, "Taunting")) {
    root.classList.add("card--taunt");
    const shield = document.createElement("span");
    shield.className = "card__taunt";
    shield.setAttribute("aria-hidden", "true");
    // A shield glyph, not the word: the card face is already dense and this must read at mini size.
    shield.textContent = "◆";
    root.append(shield);
    root.dataset["taunt"] = "1";
  }

  /**
   * ⭐ **EVERY OTHER CONDITION THE CREATURE IS WEARING.** Taunt got a badge above because it changes
   * whom you may attack; a granted buff, a stun or a silence changes the game just as much and had
   * NO mark anywhere. See `liveConditions` for why the applied array is its own filter and why
   * `Disabled` is load-bearing.
   *
   * ⛔ **TAUNT IS EXCLUDED HERE, not drawn twice.** It already has a dedicated shield in the corner
   * that the targeting rules refer to; a second chip saying the same thing is the kind of duplicate
   * signal that makes a dense card face unreadable.
   *
   * ⚠️ **THE NAME IS THE SERVED ONE.** `KEYWORD_INFO` comes from the database's own `keywords[]`,
   * so a player can say what they are looking at, which is the third of `revival-mechanics`'s three
   * questions. A chip with an invented label would be a second owner of a mechanic's name.
   *
   * ⚠️ **A STRIP, NOT A CORNER.** All four of the card's corners are taken (cost, taunt, attack,
   * health) and `card.ts` has already paid twice for putting a badge in an occupied one. This sits
   * along the top edge, between them.
   */
  const conditions = liveConditions(c).filter((k) => k !== "Taunting");
  if (conditions.length > 0) {
    const strip = document.createElement("div");
    strip.className = "card__conditions";
    for (const kw of conditions) {
      const info = KEYWORD_INFO[kw];
      const chip = document.createElement("span");
      chip.className = "card__condition";
      // The served title, or the raw keyword if the glossary has no entry. ⚠️ Never blank: a chip
      // with no text is a coloured dot, which is precisely what typed Foxfire shipped as.
      chip.textContent = info?.title || kw;
      if (info?.text) chip.title = `${info.title}: ${info.text}`;
      strip.append(chip);
    }
    root.append(strip);
    root.dataset["conditions"] = String(conditions.length);
  }

  if (opts.selectable) {
    (root as HTMLButtonElement).type = "button";
    // ⚠️ A hover sound on EVERY card would be a machine gun across a 7-card hand, so it is quiet and
    // only on cards the player can actually act on.
    root.addEventListener("pointerenter", () => {
      if (!(root as HTMLButtonElement).disabled) audio.play("cardHover", 0.35);
    });
    root.dataset["label"] = name;
    root.classList.toggle("card--selected", !!opts.selected);
    // 🚨 **THE REPLACE BADGE IS A MULLIGAN AFFORDANCE AND MUST NOT LEAK INTO THE GAME.**
    // It was styled off `.card--selected`, and selecting a creature to ATTACK also uses that class -
    // so mid-match a chosen attacker wore the word REPLACE. Alex, 2026-08-14: *"why is there a
    // replace option on this page? this is in game there is no replace option in game. You are still
    // not following the rules."* A shared state class is a shared meaning; the two are now distinct.
    root.classList.toggle("card--replacing", !!opts.selected && !!opts.replacing);
    root.setAttribute("aria-pressed", String(!!opts.selected));
    if (opts.playable !== undefined) {
      root.classList.toggle("card--playable", opts.playable);
      root.classList.toggle("card--unaffordable", !opts.playable);
      (root as HTMLButtonElement).disabled = !opts.playable;
      // ⛔ **SAY WHICH GATE REFUSED. THEY ARE TWO GATES AND THIS SAID ONE THING FOR BOTH.**
      //
      // Playability is `canAfford && colourOk`. A card can be refused for the scalar cost OR for its
      // COLOUR, and until 2026-08-17 both rendered as "Not enough Foxfire" - which is FALSE in the
      // colour case and reads as a broken game.
      //
      // 🔴 Alex hit it in the hero campaign: *"i played a card, then i burned a card for foxfire to
      // play a second card but i couldnt play the second card."* Burning grants ONE token of the
      // burned card's OWN colour, so it does not help a card of a different realm. He had the
      // Foxfire. He was told he did not.
      //
      // ⭐ The server has always sent the exact reason per card ("needs 2 The_Verdant_Hollow
      // Foxfire, holds 1"); the client threw it away. A refusal the player cannot act on is the
      // same defect as no refusal at all.
      if (!opts.playable) {
        root.title = opts.refusal || "Not enough Foxfire";
        /* 🔴 **THE SERVER HAS ALWAYS SENT THE EXACT REASON AND THE PLAYER HAS NEVER SEEN IT.** It
           went into a `title`, and tooltips moved to right-click on 2026-08-18 - so the one sentence
           that explains a refused play became unreachable unless you already knew to go looking.
           
           Alex, the same afternoon, three times: *"i am trying to play the wolf why cant i play
           it"*, *"why cant i play a card now?"*, and then *"also the game seems frozen"*. The server
           log for that match says `foxfire REFUSED ... needs 1 The_Verdant_Hollow Foxfire, holds 0`
           and his turn then expired on the clock, twice more. **The answer existed, was correct, and
           was invisible.**
           
           ⚠️ Short form only. The full sentence stays in the tip; this is a label on a card at hand
           size, so it says WHICH GATE refused, not the whole rule. */
        const why = document.createElement("span");
        why.className = "card__refusal";
        const colourGate = /realm|token|Verdant|Negafook|Spirit|Scorched|Ironworks/i.test(opts.refusal ?? "");
        why.textContent = colourGate ? "needs a realm token" : "not enough Foxfire";
        root.append(why);
      }
    }
    if (opts.onToggle) root.addEventListener("click", opts.onToggle);
  }

  // ── the art, clipped to our mask ───────────────────────────────────────────────────────────────
  const art = document.createElement("div");
  art.className = "card__art";
  if (c.DataId) {
    // ⚠️ The art is keyed by the CARD's artUrl, which `cards.ts` resolves from the served database.
    const url = artUrlFor(c.DataId);
    if (url) art.style.backgroundImage = `url("/art/cards/${url}.webp")`;
  }

  const shine = document.createElement("div");
  shine.className = "card__shine";
  shine.setAttribute("aria-hidden", "true");

  /**
   * ⭐ **A MINTED CARD IS THE SAME PAINTING WITH A DIFFERENT FINISH.** Alex, 2026-08-16, asking
   * whether minted cards should have different artwork and then answering himself: *"or save the
   * different artwork style for an NFT sale collection?"* The second instinct is the right one.
   * Different ART breaks the one thing an ERC-1155 exists to say, *this token IS that card*: the
   * minted copy becomes a picture inspired by the card and the in-game version becomes the lesser
   * one. A different TREATMENT of the same painting is the proven pattern (Magic foils, Hearthstone
   * golden): identity preserved, one art asset, instantly readable, and it extends to a rarity
   * ladder for free.
   *
   * ⚠️ **IN A MATCH THE SERVER DECIDES, AND THE CLIENT DOES NOT GET A VOTE.** `c.IsFoil` is set by
   * `MatchSession.MarkFoils` after the per-viewer mask, so it covers BOTH seats and cannot mark a
   * card this viewer may not identify. The client briefly owned this rule and could only ever gild
   * your own cards - it has no way to know what an opponent holds, and guessing from the card id
   * would gild their copy of a card you own.
   *
   * ⭐ `opts.foil` is for surfaces OUTSIDE a match: the deck builder holds the owned entities
   * themselves and answers a question the match has no opinion on.
   */
  const foil = (opts.foil ?? c.IsFoil === true) ? document.createElement("div") : null;
  if (foil) {
    foil.className = "card__foil";
    foil.setAttribute("aria-hidden", "true");
    root.classList.add("card--foil");
  }

  // ── the cost, top-left ─────────────────────────────────────────────────────────────────────────
  const cost = document.createElement("span");
  cost.className = "card__cost";
  cost.textContent = String(stat(c.Mana));

  // ── name and rules text ────────────────────────────────────────────────────────────────────────
  // ⭐ **A TOOLTIP ON EVERY CARD, SAYING WHAT IT DOES.** Alex, 2026-08-15: *"Don't forget about the
  // tooltips"* - and the testers before him. A board card is drawn small by design (it is being
  // TRACKED, not read), so its rules text is truncated or absent; without a tooltip the only way to
  // learn what a card does is to play it and watch.
  //
  // ⚠️ **BUILT FROM THE SERVED RECORD, NOT FROM THE RENDERED TILE.** Reading the visible text back
  // would inherit whatever the layout truncated - the tooltip would repeat the problem it exists to
  // solve.
  const NEWLINE = String.fromCharCode(10);
  const rec = cardRecord(c.DataId);
  if (rec) {
    const bits: string[] = [name];
    const cost = stat(c.Mana);
    const realmName = String(rec["quadrant"] ?? "").replace(/_/g, " ");
    bits.push(`${cost} Foxfire${realmName && hasRealm(realmName) ? ` · ${realmName}` : ""}`);
    if (String(rec["type"] ?? "") === "Creature") {
      bits.push(`${stat(c.Attack)} attack · ${stat(c.Hp)} health`);
    }
    if (effect) bits.push(effect);
    // The colour cost, in words, so a player can plan which card to burn.
    const req = Object.entries(opts.requires || {}).filter(([, n]) => n > 0);
    if (req.length) {
      bits.push("Needs " + req.map(([col, n]) => `${n} ${realmShortName(col)}`).join(" + ")
                + " token" + (req.reduce((t, [, n]) => t + n, 0) > 1 ? "s" : ""));
    }
    // ⭐ THE RULE, ON EVERY CARD THAT HAS A COLOUR. One line, and it is the whole mechanic: a token
    // comes from burning a card of that realm, so the realm on this card is also what it PAYS you.
    if (hasRealm(realm)) {
      bits.push(`Burn it instead: +1 ${realmShortName(realm)} token`);
    }
    // The flavour last, and only if there is room for it to be a bonus rather than the point.
    const flavour = String(rec["tooltip"] ?? "").trim();
    if (flavour && flavour !== "None") bits.push(flavour);
    root.title = bits.filter(Boolean).join(NEWLINE);

    /* ── THE SAME FACTS, WITH THE BOARD'S OWN DEVICES IN FRONT OF THEM ─────────────────────────
     * ⭐ Alex, 2026-08-18: *"for foxfire amount it shows the orange square and number like the one
     * on the top left of the card, and then the sentence explainer."*
     *
     * ⚠️ **THE PLAIN `title` ABOVE STAYS.** It is the accessible name and the fallback, so a screen
     * reader and any surface that has not opted into rich tips both still get the whole card. This
     * adds a presentation, it does not replace the content. */
    setRichTip(root, () => {
      const box = document.createElement("div");
      box.className = "tip__card";

      const head = document.createElement("div");
      head.className = "tip__title";
      head.textContent = name;
      box.append(head);

      const row = (icon: HTMLElement | null, text: string, cls = ""): void => {
        const r = document.createElement("div");
        r.className = "tip__row" + (cls ? ` ${cls}` : "");
        if (icon) r.append(icon);
        const t = document.createElement("span");
        t.className = "tip__text";
        t.textContent = text;
        r.append(t);
        box.append(r);
      };
      const chip = (cls: string, label: string): HTMLElement => {
        const e = document.createElement("span");
        e.className = cls;
        e.textContent = label;
        return e;
      };

      // The cost, wearing the card's own orange square.
      row(chip("tip__cost", String(cost)),
          `Foxfire to play${realmName && hasRealm(realmName) ? `, and it is a ${realmName} card` : ""}`);

      if (String(rec["type"] ?? "") === "Creature") {
        const pair = document.createElement("span");
        pair.className = "tip__pair";
        pair.append(chip("tip__atk", String(stat(c.Attack))), chip("tip__hp", String(stat(c.Hp))));
        row(pair, "attack and health");
      }

      if (effect) row(chip("tip__keyword", "◆"), effect, "tip__row--effect");

      if (req.length) {
        const pips = document.createElement("span");
        pips.className = "tip__pips";
        for (const [col, n] of req)
          for (let i = 0; i < n; i++) {
            const pip = chip("tip__pip", "");
            pip.style.setProperty("--pip", `var(--realm-${col.toLowerCase()}, var(--accent))`);
            pips.append(pip);
          }
        row(pips, "tokens of its realm needed before you can play it");
      }

      if (hasRealm(realm)) {
        const pip = chip("tip__pip", "");
        pip.style.setProperty("--pip", `var(--realm-${realm.toLowerCase()}, var(--accent))`);
        row(pip, `Burn it instead and gain 1 ${realmShortName(realm)} token`);
      }

      /* ⭐ **WHO THE CARD BELONGS TO.** DayDevil, beta feedback 2026-08-20: *"make sure they have a
       * clear faction identity."* Faction was in the served data on every card and rendered on NO
       * surface, so a player could not perceive it at all - which makes faction-level design work
       * unobservable no matter what the data says.
       *
       * ⛔ **IT GOES IN THE TOOLTIP, NOT ON THE CARD FACE.** All four corners of the card are taken
       * (cost, realm, attack, health) and the last thing dropped onto an occupied corner covered
       * the stats on every creature in play. The tooltip is where a player already inspects a card
       * and it has the room.
       *
       * ⚠️ **RENAMED THROUGH `displayName`, NEVER PRINTED RAW.** The served value is the ORIGINAL
       * studio's identifier - nine of our ten factions still carry theirs, because a faction value
       * is an enum the engine deserialises and cannot be renamed in data. Printing it raw would put
       * "Company_of_Hoplites" on screen. `display-names.generated.ts` is generated from the theme
       * workbook by `tools/gen_display_names.py`. */
      if (factionName) row(chip("tip__keyword", "❖"), factionName, "tip__row--faction");

      if (flavour && flavour !== "None") {
        const f = document.createElement("div");
        f.className = "tip__flavour";
        f.textContent = flavour;
        box.append(f);
      }
      return box;
    });
  }

  /* Resolved once and shared by the card face and the rich tooltip, so the two cannot disagree. */
  const factionName = (() => { const f = cardFaction(c.DataId); return f ? displayName(f) : undefined; })();

  const title = document.createElement("span");
  title.className = "card__name";
  // ⚠️ textContent. Card titles are served data, and served data is not markup.
  title.textContent = name;

  /*
   * ⭐ **THE FACTION, ON THE CARD FACE.** Alex, 2026-08-20: *"the card must also show it, it can not
   * be in the tooltips only."* He is right, and it is a lesson this project already paid for - a
   * fact that lives only in a `title` tooltip is not communicated to anybody.
   *
   * ⛔ **IT GOES IN THE TEXT BLOCK'S FLOW, NOT ON A CORNER.** All four corners are taken (cost,
   * realm, attack, health) and the last element dropped onto one covered the stats on every
   * creature in play. `.card__text` is a flex column and "a flow layout cannot overlap itself".
   *
   * ⚠️ **RENAMED, NEVER RAW.** The served value is the original studio's identifier - nine of ten
   * factions still carry theirs, because the engine deserialises it as an enum. `displayName` comes
   * from the theme workbook via `tools/gen_display_names.py`.
   */
  const factionEl = document.createElement("span");
  factionEl.className = "card__faction";
  factionEl.textContent = factionName ?? "";
  if (!factionName) factionEl.hidden = true;

  const rules = document.createElement("span");
  rules.className = "card__effect";
  rules.textContent = effect;
  // ⚠️ A mini card keeps its rules text in the DOM but hidden, so a tooltip or a hold-to-read can
  // use it without a second lookup - and so a screen reader still reaches it.
  if (opts.mini) rules.hidden = true;

  // ── the badges ─────────────────────────────────────────────────────────────────────────────────
  const atk = document.createElement("span");
  atk.className = "card__attack";
  atk.textContent = String(stat(c.Attack));

  const hp = document.createElement("span");
  hp.className = "card__health";
  hp.textContent = String(stat(c.Hp));

  // ⚠️ Name and effect share ONE flowing block so they cannot overlap at small card sizes.
  const text = document.createElement("span");
  text.className = "card__text";
  // ── THE REALM, NAMED AND SIZED SO NOBODY HAS TO HOVER ────────────────────────────────────────
  //
  // 🔴 Alex, 2026-08-17: *"The card colours are not obvious enough, and they have to every time read
  // a small tooltip to find out. That is poor UI/UX."* He is right, and it was worse than subtle:
  // the realm was a 3px band on the top edge and a tinted cost digit. Typed Foxfire makes the realm
  // the thing that PAYS for the card, so it is the second fact a player reads after the cost, and it
  // was the least visible thing on the card.
  //
  // ⛔ **AND COLOUR ALONE IS NOT A SIGNAL.** Two of our six realms are a mid green and a mid blue;
  // to a red-green colour-blind player (~8% of men) those and Neutral converge. A swatch WITH ITS
  // NAME is readable by everyone and needs no legend, no tooltip and no memorisation.
  //
  // ⭐ The requirement pips answer the question that actually blocked a play: not "what realm is
  // this" but "how much of that realm do I need". They are the same count the server enforces.
  const chip = document.createElement("span");
  chip.className = "card__realm";
  if (realm) {
    // ⛔ NO SWATCH INSIDE THE CHIP. The left rail already carries the colour at far greater size, so
    // a 9px square beside the word bought nothing and cost the width that then truncated the word
    // itself ("SCORC...", "NEGAF...", "IRONW..."). The NAME is the part a colour-blind player needs;
    // spending pixels on a second copy of the colour to clip the name is the wrong trade.
    const label = document.createElement("span");
    label.className = "card__realm-name";
    label.textContent = realmShortName(realm);
    chip.append(label);

    // One pip per token of this realm the card demands. Absent when it demands none, so a cheap
    // card is not decorated with an empty row.
    const need = Number((opts.requires || {})[realm] || 0);
    if (need > 0) {
      const pips = document.createElement("span");
      pips.className = "card__realm-pips";
      pips.setAttribute("aria-label", `needs ${need} ${realmShortName(realm)} Foxfire`);
      for (let i = 0; i < need; i++) pips.append(document.createElement("b"));
      chip.append(pips);
    }
  }

  text.append(title, rules);
  /* ⛔ **APPENDED TO THE CARD ROOT, NOT TO `.card__text`.** `.card__text` is itself
   * `position: absolute` with `overflow: hidden`, so an absolutely-positioned child of it resolves
   * against THAT 24.8px box and is clipped to nothing - measured `paintedH: 0` while the DOM
   * reported the element "visible". The faction chip must hang off the card, beside the realm chip. */
  root.append(factionEl);

  /*
   * ⭐ **THE TRIBES, ON THE CARD FACE.** Until now the card rendered name, cost, art, rules, attack,
   * health, realm and faction - and never `subTypes`. A synergy payoff that counts `Undead` gave
   * the player nothing to count, because the game never told them which creatures were Undead.
   *
   * ⛔ **A TRIBE THE PLAYER CANNOT SEE IS NOT IDENTITY**, and this project has already paid for that
   * exact shape twice: the de-branded faction names were correct in a file and rendered nowhere,
   * and three player answers sat in `title` tooltips nobody reads. Owner:
   * `.claude/skills/revival-realm-identity/SKILL.md` §6.
   *
   * ⚠️ Same placement reasoning as the faction chip above - hung off the card ROOT, never inside
   * `.card__text`, which is `position: absolute; overflow: hidden` and clips an absolute child to
   * nothing while the DOM still reports it visible.
   *
   * ⚠️ RENAMED, NEVER RAW: a subtype is a served key, so it goes through `displayName` like every
   * other served string. A mini card hides it - at that size it would be unreadable and would only
   * crowd the art - but it stays in the DOM so a tooltip and a screen reader still reach it.
   */
  const tribes = cardSubTypes(c.DataId);
  const tribeEl = document.createElement("span");
  tribeEl.className = "card__tribes";
  tribeEl.textContent = tribes.map((t) => displayName(t)).join(" · ");
  /* ⛔ **`hidden` ONLY WHEN THERE IS NOTHING TO SAY. "Too small to read" is the CONTAINER QUERY's
   * job, and it must not also be decided here.**
   *
   * The first version set `hidden` for `opts.mini` as well, which is two owners of one question and
   * contradicted the comment right above it: `hidden` removes the element from the accessibility
   * tree, so a screen reader could NOT reach it - the exact opposite of what was claimed. The query
   * hides it visually while leaving it readable, which is why the faction chip uses the same
   * mechanism and why "is there room" beats "is this the hand" as the rule. */
  if (!tribes.length) tribeEl.hidden = true;
  root.append(tribeEl);

  // ⛔ THE CHIP SITS WITH THE COST, NOT IN THE TEXT BLOCK. Putting it under the card name cost a
  // line of RULES TEXT, which on a card whose whole point is its rules is the wrong trade - the
  // effect line was clipped entirely on any card with a two-line name
  // (`docs/captures/web/card-realms.png`, second pass).
  //
  // ⭐ And it belongs here anyway: `.card__cost` is already tinted the realm colour because "cost
  // and realm are the two facts you weigh together". This puts them side by side instead of at
  // opposite ends of the card.
  // ⚠️ The foil goes ABOVE the art and the gloss and BELOW every number and label. A finish that
  // washes over the cost or the health is a downgrade wearing a reward's clothes.
  root.append(art, shine, ...(foil ? [foil] : []), cost, chip, text, atk, hp);

  if (opts.dragId !== undefined) {
    root.draggable = true;
    root.addEventListener("dragstart", (e) => {
      (e as DragEvent).dataTransfer?.setData("text/plain", String(opts.dragId));
      root.classList.add("card--dragging");
      audio.play("cardPick");
    });
    root.addEventListener("dragend", () => root.classList.remove("card--dragging"));
  }

  // ⭐ THE FOXFIRE BURN. A separate control, and it stops the click reaching the card underneath -
  // burning a card you meant to play would be unrecoverable.
  if (opts.onBurn) {
    const burn = document.createElement("span");
    burn.className = "card__burn";
    burn.setAttribute("role", "button");
    burn.tabIndex = 0;
    burn.dataset["label"] = "BURN";
    // 🔴 **SAY WHAT BURNING THIS CARD GIVES YOU, INCLUDING THE COLOUR.** Alex, 2026-08-17: *"the
    // mechanic of burning x card to replenish foxfire for x colour is not clear even to me."* This
    // label said "Feed to the Foxfire for 3" - the SCALAR only - so the single most important fact
    // about the mechanic, that you get a token of THIS CARD'S colour and no other, appeared nowhere
    // in the interface. A player cannot choose what to burn if burning does not say what it yields.
    // ⚠️ **`realm` IS TRUTHY FOR A NEUTRAL CARD, SO THIS PROMISED A "+1 Neutral token".** No card in
    // the game requires one - `Foxfire.Requirement()` returns empty for Neutral, deliberately - so
    // that promise was for a currency that buys nothing, and the rail then drew it beside the real
    // tokens. It is the concrete half of the player report Alex relayed: *"why sometimes it works
    // and sometimes it doesn't."* The card's own tooltip already guarded this; the burn control did
    // not, which is how one rule ended up stated two different ways on one card.
    burn.title = hasRealm(realm)
      ? `Feed to the Foxfire: +1 ${realmShortName(realm)} token, and +${stat(c.Mana)} Foxfire to spend`
      : `Feed to the Foxfire: +${stat(c.Mana)} Foxfire to spend (Neutral cards give no token)`;
    /* 🚨 **THE ANSWER WAS IN A TOOLTIP, AND TOOLTIPS ARE NOW RIGHT-CLICK ONLY.** Alex, 2026-08-18:
       *"i burned a neutral card, there is no way a player knows that burning a neutral card does not
       allow you to play the green cards."*

       He is right, and the history here is the point: the `title` above ALREADY said "(Neutral cards
       give no token)". It was written for exactly this confusion, it was correct, and he still hit
       the confusion - because the same day we moved tips to right-click, so the sentence became
       unreachable unless you already suspected there was something to read. **A fact that only
       appears when you go looking for it does not prevent the mistake it was written to prevent.**

       ⭐ So the yield goes ON THE CONTROL, always visible, before the click. A realm card shows the
       token it pays; a Neutral card says plainly that it pays none. */
    burn.textContent = "";
    const word = document.createElement("span");
    word.className = "card__burnword";
    word.textContent = "BURN";
    burn.append(word);

    const yield_ = document.createElement("span");
    yield_.className = "card__burnyield";
    if (hasRealm(realm)) {
      yield_.classList.add("card__burnyield--token");
      yield_.style.setProperty("--pip", `var(--realm-${realm.toLowerCase()}, var(--accent))`);
      // ⚠️ **NAME THE REALM.** A bare "+1" says a number and not a currency, and the whole confusion
      // Alex hit is that tokens are PER REALM: *"there is no way a player knows that burning a
      // neutral card does not allow you to play the green cards."*
      yield_.textContent = `+1 ${realmShortName(realm)}`;
    } else {
      // ⚠️ The WORD, not a struck-through pip. "no token" is unambiguous at card size; a crossed-out
      // symbol is a puzzle, and this is the exact place a player must not have to solve one.
      yield_.classList.add("card__burnyield--none");
      yield_.textContent = "no token";
    }
    burn.append(yield_);
    const fire = (e: Event) => {
      e.stopPropagation();
      e.preventDefault();
      opts.onBurn?.();
    };
    burn.addEventListener("click", fire);
    burn.addEventListener("keydown", (e) => {
      if ((e as KeyboardEvent).key === "Enter" || (e as KeyboardEvent).key === " ") fire(e);
    });
    root.append(burn);
  }

  // ⚠️ A SPELL HAS NO ATTACK OR HEALTH, and drawing 0/0 badges on one is a card that looks broken.
  if (c.Type === "Spell") {
    atk.hidden = true;
    hp.hidden = true;
    root.classList.add("card--spell");
  }

  /*
   * 🚨 **`Building` IS A REAL CARD TYPE AND THIS RENDERER HAS NEVER KNOWN ABOUT IT.** Measured in
   * the served database: 171 cards are 121 Creature, 43 Spell and **7 Building**. Every Building has
   * `attack: 0` and `health: null`, and the code above only special-cased Spell - so all seven drew
   * with a 0 attack badge and an empty health badge, which is precisely what a DEAD creature looks
   * like. `Wall of the Restless` is a 5-cost taunting barricade and it rendered as a 0/0.
   *
   * Found while researching Alex's zoning request, which is the point of researching before drawing:
   * one of the "zones" he wants turned out to be a card type the game had never distinguished at all.
   */
  if (c.Type === "Building") {
    atk.hidden = true;
    hp.hidden = true;
    root.classList.add("card--building");
  }

  /*
   * ── THE ROLE PLINTH ─────────────────────────────────────────────────────────────────────────
   * ⭐ Alex, 2026-08-18: *"can we group cards that are attack, or defend or spell or heal cards and
   * let it be very clear to the player what it belongs to? So maybe zones."*
   *
   * ⛔ **THE CARDS CANNOT MOVE** - 10 of 171 carry adjacency effects, so grouping the row physically
   * would change what `rally-adjacent` and `heal-max-adjacent` hit. **So a zone is a colour, not a
   * place**: the eye groups along the row and every card stays where the engine put it. See
   * `roleOf` in `match/rules.ts` for the derivation and the evidence.
   */
  if (opts.role) {
    root.classList.add(`card--role-${opts.role}`);
    const plinth = document.createElement("span");
    plinth.className = "card__role";
    plinth.textContent = roleLabel(opts.role);
    root.append(plinth);
  }

  return root;
}

/** artUrl for a card id, resolved from the served database. */
let artIndex: Map<string, string> | null = null;

export function setArtIndex(index: Map<string, string>): void {
  artIndex = index;
}

function artUrlFor(dataId: string): string | undefined {
  return artIndex?.get(String(dataId));
}


// ⭐ **THE ZOOM'S RENDERER, HANDED OVER ONCE.**
//
// ⚠️ Injected rather than imported by `cardZoom` directly: the two modules would otherwise import
// each other, and Vite resolves that cycle to `undefined` at runtime instead of failing the build -
// a blank preview with no error anywhere to explain it.
setZoomRenderer((c) => {
  // 🚨 **THE PREVIEW USES THE *MINI* LAYOUT, ENLARGED - NOT THE `full` ONE, AND THAT IS MEASURED.**
  //
  // `card-frames.generated.ts` calls `full` "the large inspect view", so it looked purpose-built for
  // exactly this. It is not: `full_*.png` and `regular_*.png` are the SAME flame motif, and neither
  // is a card border. At 17rem the silhouette stops reading as a frame and becomes a large black
  // wedge across the middle of the card. Checked by opening the assets, after the first render
  // looked wrong - the NAME of an asset is not a description of it, which is the same lesson the
  // `artUrl`-not-`title` note above records.
  //
  // The mini layout is the one that actually reads at size: art, name, cost and badges. What it
  // omits is the RULES TEXT, which is the whole point of inspecting a card - so that is added back
  // below rather than inherited from a layout that does not work.
  const wrap = document.createElement("div");
  wrap.className = "cardzoom__body";
  wrap.append(cardEl(c, { mini: true, noZoom: true }));

  const rules = cardEffect(c.DataId);
  if (rules) {
    const t = document.createElement("p");
    t.className = "cardzoom__rules";
    t.textContent = rules;
    wrap.append(t);
  }
  // ⭐ **WHERE YOU WIN IT, ON THE CARD ITSELF.** Alex, 2026-08-15: *"maybe somewhere like a tooltip
  // it says where it is winnable (what campaign level)."* The forward fact - which stage gives which
  // card - exists in the campaign data; the reverse index is generated, never typed.
  //
  // ⚠️ Only the HERO campaign names its cards. A card with no named source is NOT unobtainable, so
  // the absence says nothing and no line is drawn - claiming "not winnable" would be false.
  const won = CARD_SOURCES[String(c.DataId ?? "")];
  const w = document.createElement("p");
  w.className = "cardzoom__won";
  if (won && won.length) {
    /*
     * ⚠️ "WIN IT AT", NOT "WON AT". This line describes HOW a card is obtained; it is not a receipt
     * for the copy in front of you. It read "Won at ..." and Alex, looking at cards he holds from
     * the STARTER DECK, said *"there are a number of cards in my owned inventory that say won ..."*
     * and then reasonably asked why the Tent did not list them. The Tent was right — those were
     * never campaign wins — and this sentence was what created the expectation. Every string here
     * must read as instructions.
     */
    const first = won[0]!;
    w.textContent = `Win it at ${first.campaign}, stage ${first.stage}: ${first.title}`;
  } else {
    /*
     * 🔴 THE OTHER 166 CARDS USED TO SAY NOTHING, AND THAT WAS THE BUG.
     *
     * `RANDOM_CARD_SOURCE` has existed as the one-owner answer since this index was generated, and
     * the ONLY place it was ever rendered was the title attribute of a filter BUTTON. So a player
     * inspecting any of the 166 cards the hero campaign does not name got silence — and silence
     * reads as "there is no way to get this", which is the opposite of true: those cards come from
     * roughly 88 random draws, which is how a player actually gets most of their collection.
     *
     * Alex, queue #9: *"winnable-card sorting + tooltips saying WHERE each card is won."* The answer
     * for most of the set is "from the pool, and here is what fills it" — not nothing.
     */
    w.textContent = RANDOM_CARD_SOURCE;
    w.classList.add("cardzoom__won--pool");
  }
  wrap.append(w);

  const flavour = cardRecord(c.DataId)?.tooltip;
  if (flavour) {
    // ⚠️ `tooltip` is the FLAVOUR and `description` is the RULES - the original's field names are
    // inverted, which `cards.ts` calls the most expensive naming trap in the project.
    const f = document.createElement("p");
    f.className = "cardzoom__flavour";
    f.textContent = String(flavour);
    wrap.append(f);
  }
  return wrap;
});
